import React from "react";

const SellDeviceSkeleton = ({ isDarkMode }) => {
  return (
    <div
      className={`rounded-xl overflow-hidden shadow-sm ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className={`p-3 md:p-8 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        {/* Mobile Layout Skeleton */}
        <div className="block md:hidden">
          {/* Product Header with Image and Title */}
          <div className="flex gap-3">
            <div className="w-1/3">
              <div
                className={`w-full h-auto aspect-square rounded-xl ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-200"
                } animate-pulse`}
              ></div>
            </div>
            <div className="w-2/3">
              <div
                className={`h-5 w-40 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-1`}
              ></div>
              <div className="flex flex-col mt-1">
                <div
                  className={`h-3 w-20 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-1`}
                ></div>
                <div className="flex items-center justify-between mt-1">
                  <div
                    className={`h-8 w-28 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                  <div
                    className={`h-8 w-24 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded-lg animate-pulse`}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Cards Grid - 3 in a row */}
          <div className="grid grid-cols-3 gap-2 my-6">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-lg border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-5 h-5 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                ></div>
                <div
                  className={`h-3 w-16 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                ></div>
              </div>
            ))}
          </div>

          {/* Sell Now Button */}
          <div
            className={`w-full h-12 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded-xl animate-pulse`}
          ></div>

          {/* Fixed Bottom Bar for Mobile */}
          <div className="fixed bottom-0 left-0 right-0 z-50">
            <div
              className={`shadow-xl border-t ${
                isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
              }`}
            >
              {/* Price Summary Row */}
              <div
                className={`px-4 py-3 flex justify-between items-center border-b ${
                  isDarkMode ? "border-gray-800" : "border-gray-100"
                }`}
              >
                <div>
                  <div
                    className={`h-3 w-16 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-1`}
                  ></div>
                  <div
                    className={`h-7 w-24 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                </div>
                <div className="text-right">
                  <div
                    className={`h-3 w-24 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-1`}
                  ></div>
                  <div
                    className={`h-5 w-12 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="flex">
                <div
                  className={`flex-1 h-12 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"} animate-pulse`}
                ></div>
                <div
                  className={`flex-1 h-12 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout Skeleton - Matches exactly */}
        <div className="hidden gap-6 md:grid md:grid-cols-3">
          {/* Left Section - Product Details (spans 2 columns) */}
          <div className="md:col-span-2">
            <div
              className={`grid grid-cols-2 gap-5 p-5 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              } shadow-sm border ${
                isDarkMode ? "border-gray-700" : "border-gray-100"
              }`}
            >
              {/* Product Image Skeleton */}
              <div
                className={`overflow-hidden rounded-lg ${
                  isDarkMode ? "bg-gray-900" : "bg-gray-50"
                }`}
              >
                <div className="w-full h-[250px] animate-pulse"></div>
              </div>

              {/* Product Info Skeleton */}
              <div>
                {/* Title Skeleton */}
                <div
                  className={`h-8 w-48 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-3`}
                ></div>

                <div className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <div>
                      <div
                        className={`h-3 w-20 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-1`}
                      ></div>
                      <div
                        className={`h-9 w-32 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                      ></div>
                    </div>
                    <div
                      className={`h-9 w-28 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded-lg animate-pulse`}
                    ></div>
                  </div>
                </div>

                {/* Feature Cards Skeleton */}
                <div className="grid grid-cols-3 gap-3 mt-6">
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className={`flex flex-col items-center justify-center gap-2 px-3 py-4 rounded-lg border ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                      ></div>
                      <div
                        className={`h-3 w-16 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Price Card (spans 1 column) */}
          <div className="md:col-span-1">
            <div
              className={`p-6 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              } shadow-sm border ${
                isDarkMode ? "border-gray-700" : "border-gray-100"
              }`}
            >
              {/* Price Summary Heading Skeleton */}
              <div
                className={`h-6 w-32 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse mb-5`}
              ></div>

              <div className="space-y-4">
                {/* Selling Price Row */}
                <div className="flex items-center justify-between">
                  <div
                    className={`h-4 w-24 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                  <div
                    className={`h-6 w-28 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                </div>

                {/* Express Pickup Row */}
                <div className="flex items-center justify-between">
                  <div
                    className={`h-4 w-28 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                  <div
                    className={`h-5 w-12 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                </div>

                {/* Divider */}
                <div
                  className={`h-px my-3 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                ></div>

                {/* Total Price Row */}
                <div className="flex items-center justify-between pt-2">
                  <div
                    className={`h-5 w-24 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                  <div
                    className={`h-7 w-32 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                  ></div>
                </div>

                {/* Sell Now Button Skeleton */}
                <div
                  className={`w-full h-12 mt-4 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded-lg animate-pulse`}
                ></div>

                {/* Premium subtle badge */}
                <div
                  className={`h-3 w-40 mx-auto ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded animate-pulse`}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellDeviceSkeleton;